import { useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography
} from "@mui/material"
import { toast } from "react-toastify"

import {
  IntegrationProductsDocument,
  useAddIntegrationProductsMutation,
  useIntegrationProductSearchLazyQuery,
  useSetIntegrationProductMutation
} from "src/graphql/quark/quark.type"
import { readWholeNumber } from "../stockBuffer"

type Candidate = {
  productId: string
  name: string
  barcode?: string | null
  active: boolean
}

const candidateDetails = (product: Candidate) =>
  [product.barcode ? `Barcode ${product.barcode}` : null, product.active ? null : "Inactive"]
    .filter(Boolean)
    .join(" · ")

/**
 * Two explicit steps: pick the product, then type the SKU the integration
 * knows it by. Nothing is matched or suggested automatically. Stock buffer and
 * max sales quantity are optional and can be changed later.
 *
 * Channels that create products from ONE need no SKU, so there the operator
 * just ticks the products to list.
 */
const AddProductModal = ({
  connectionId,
  integrationName,
  defaultStockBuffer,
  createsProducts,
  showMaxSales,
  open,
  onClose
}: {
  connectionId: string
  integrationName: string
  defaultStockBuffer: number | null
  createsProducts: boolean
  showMaxSales: boolean
  open: boolean
  onClose: () => void
}) => {
  const [term, setTerm] = useState("")
  const [selected, setSelected] = useState<Candidate | null>(null)
  const [picked, setPicked] = useState<Record<string, Candidate>>({})
  const [sku, setSku] = useState("")
  const [buffer, setBuffer] = useState("")
  const [maxSales, setMaxSales] = useState("")

  const bufferField = readWholeNumber(buffer)
  const maxSalesField = readWholeNumber(maxSales, 1)

  const [search, { data, loading }] = useIntegrationProductSearchLazyQuery({
    fetchPolicy: "network-only"
  })

  const [setProduct, { loading: saving }] = useSetIntegrationProductMutation({
    refetchQueries: [
      { query: IntegrationProductsDocument, variables: { connectionId } }
    ]
  })
  const [addProducts, { loading: adding }] = useAddIntegrationProductsMutation({
    refetchQueries: [{ query: IntegrationProductsDocument, variables: { connectionId } }]
  })

  const handleClose = () => {
    setTerm("")
    setSelected(null)
    setPicked({})
    setSku("")
    setBuffer("")
    setMaxSales("")
    onClose()
  }

  const handleSearch = () => {
    if (term.trim().length < 2) {
      toast.info("Type at least two characters to search.")
      return
    }
    void search({ variables: { query: term.trim() } })
  }

  const handleSave = async () => {
    if (!selected || !sku.trim() || bufferField.invalid || maxSalesField.invalid) return

    try {
      await setProduct({
        variables: {
          input: {
            connectionId,
            productId: selected.productId,
            externalSku: sku.trim(),
            stockBuffer: bufferField.value,
            maximumSalesQuantity: maxSalesField.value
          }
        }
      })
      toast.success(`${selected.name} will publish as ${sku.trim()}`)
      handleClose()
    } catch (error: any) {
      toast.error(error?.message ?? "Could not add the product")
    }
  }

  const togglePicked = (product: Candidate) =>
    setPicked((prev) => {
      const next = { ...prev }
      if (next[product.productId]) delete next[product.productId]
      else next[product.productId] = product
      return next
    })

  const pickedList = Object.values(picked)

  const handleAdd = async () => {
    if (pickedList.length === 0) return

    try {
      await addProducts({
        variables: { input: { connectionId, productIds: pickedList.map((p) => p.productId) } }
      })
      toast.success(
        pickedList.length === 1
          ? `${pickedList[0].name} will be created on ${integrationName}`
          : `${pickedList.length} products will be created on ${integrationName}`
      )
      handleClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add the products")
    }
  }

  const results: Candidate[] = data?.fusionIntegrationProductSearch ?? []

  if (createsProducts)
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add products</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Ticked products are created on {integrationName} with their name, price and SKU, and their stock is kept up
            to date there.
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              size="small"
              autoFocus
              label="Search by name or barcode"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button variant="outlined" onClick={handleSearch} disabled={loading}>
              Search
            </Button>
          </Box>

          <List dense sx={{ maxHeight: 280, overflow: "auto" }}>
            {results.map((product) => (
              <ListItemButton key={product.productId} onClick={() => togglePicked(product)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" size="small" disableRipple checked={!!picked[product.productId]} />
                </ListItemIcon>
                <ListItemText primary={product.name} secondary={candidateDetails(product)} />
              </ListItemButton>
            ))}
            {!loading && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" p={2}>
                No products yet — search above.
              </Typography>
            )}
          </List>

          {pickedList.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Selected: {pickedList.map((p) => p.name).join(", ")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleAdd()} disabled={pickedList.length === 0 || adding}>
            {pickedList.length > 1 ? `Add ${pickedList.length} products` : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    )

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add product</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            label="Search by name or barcode"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button variant="outlined" onClick={handleSearch} disabled={loading}>
            Search
          </Button>
        </Box>

        {!selected && (
          <List dense sx={{ maxHeight: 280, overflow: "auto" }}>
            {results.map((product) => (
              <ListItemButton
                key={product.productId}
                onClick={() => setSelected(product)}
              >
                <ListItemText primary={product.name} secondary={candidateDetails(product)} />
              </ListItemButton>
            ))}
            {!loading && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" p={2}>
                No products yet — search above.
              </Typography>
            )}
          </List>
        )}

        {selected && (
          <Box>
            <Typography variant="subtitle2">{selected.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.barcode ? `Barcode ${selected.barcode}` : "No barcode"}
            </Typography>
            {!selected.active && (
              <Typography variant="caption" color="warning.main" display="block">
                This product is inactive and will be published as unavailable.
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="SKU on this integration"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="Stock buffer (optional)"
              value={buffer}
              onChange={(e) => setBuffer(e.target.value)}
              inputProps={{ inputMode: "numeric" }}
              error={bufferField.invalid}
              helperText={
                bufferField.invalid
                  ? "Enter a whole number of units, or leave it empty."
                  : `Units kept off ${integrationName}. Empty uses ${
                      defaultStockBuffer != null ? `the integration's buffer (${defaultStockBuffer})` : "no buffer"
                    }.`
              }
            />
            {showMaxSales && (
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 2 }}
                label="Max per order (optional)"
                value={maxSales}
                onChange={(e) => setMaxSales(e.target.value)}
                inputProps={{ inputMode: "numeric" }}
                error={maxSalesField.invalid}
                helperText={
                  maxSalesField.invalid
                    ? "Enter a whole number from 1, or leave it empty."
                    : "Most units one order may take, never more than the stock on offer. Empty means no limit."
                }
              />
            )}
            <Button size="small" sx={{ mt: 1 }} onClick={() => setSelected(null)}>
              Choose a different product
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!selected || !sku.trim() || bufferField.invalid || maxSalesField.invalid || saving}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddProductModal
